/**
 * Zod schema skeletons — the single validator set reused by Edge Functions and
 * clients (doc 00 §2 / §6.4). Run 0 seeds a money schema + the error-envelope
 * schema; real request/response validators grow in their owning runs.
 */

import { z } from 'zod';

import { ERROR_CODES } from '../errors.js';

/** Money = integer minor units (денари). Mirrors @gym/shared/money. */
export const moneySchema = z
  .number()
  .int('Money must be an integer (minor units / денари)')
  .safe('Money exceeds the safe integer range');

/** Canonical error code enum, derived from the locked ERROR_CODES list. */
export const errorCodeSchema = z.enum(ERROR_CODES);

/** The locked error envelope (doc 00 §6.4). */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.record(z.string(), z.unknown()).optional(),
    request_id: z.string(),
    retriable: z.boolean(),
  }),
});

export type ErrorEnvelopeSchema = z.infer<typeof errorEnvelopeSchema>;

/** UUID-v4 idempotency key schema (doc 00 §6.3). */
export const idempotencyKeySchema = z.string().uuid();

/** Cursor-pagination page envelope (doc 00 §6.5). */
export const pageSchema = z.object({
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
  limit: z.number().int().positive(),
});

/** Generic list envelope factory: `{ data: T[], page }`. */
export function listEnvelopeSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    page: pageSchema,
  });
}

export * from './fiscal.js';
export * from './payments.js';
export * from './checkin.js';
export * from './retail.js';
export * from './bookings.js';
export * from './campaigns.js';
export * from './payments-online.js';
export * from './access.js';
export * from './staff-ops.js';
export * from './social.js';
